import { MutableRefObject, useEffect, useRef } from 'react';
import { WidgetMessageType, WidgetNameSource } from '../utils/type';
import { WidgetHeightMessage } from '../types/common.type';

const useResizeNotifier = (
  containerRef: MutableRefObject<HTMLDivElement | null>,
  source: WidgetNameSource,
  onHeightChange?: (message: WidgetHeightMessage) => void,
) => {
  // Keep latest callback in a ref: an inline onHeightChange from the host would
  // otherwise re-run the effect (and notify) on every host render -> render loop
  const onHeightChangeRef = useRef(onHeightChange);
  onHeightChangeRef.current = onHeightChange;

  useEffect(() => {
    if (!containerRef.current) return;

    const notify = () => {
      requestAnimationFrame(() => {
        if (!containerRef.current) return;
        const height = containerRef.current.scrollHeight;
        const message: WidgetHeightMessage = {
          type: WidgetMessageType.Height,
          source,
          height,
        };

        // Web: notify user height change
        onHeightChangeRef.current?.(message);

        // React Native WebView: post height to native layer for WebView resize
        if ((window as any).ReactNativeWebView?.postMessage) {
          (window as any).ReactNativeWebView.postMessage(
            JSON.stringify(message),
          );
          return;
        }
      });
    };

    const observer = new ResizeObserver(notify);
    observer.observe(containerRef.current);
    notify();

    return () => observer.disconnect();
  }, [containerRef, source]);
};

export default useResizeNotifier;
